import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Video, ClipboardCheck, CheckCircle2 } from "lucide-react";
import InterviewScorecard from "./InterviewScorecard";

const STAT_TONE = {
  "Requested":"bg-slate-100 text-slate-700 border-slate-200",
  "Awaiting Scheduling":"bg-sky-50 text-sky-700 border-sky-200",
  "Scheduled":"bg-sky-50 text-sky-700 border-sky-200",
  "Candidate Declined":"bg-rose-50 text-rose-700 border-rose-200",
  "Completed":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Advancing":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Second Interview Requested":"bg-sky-50 text-sky-700 border-sky-200",
  "On Hold":"bg-amber-50 text-amber-800 border-amber-200",
  "Not a Fit":"bg-rose-50 text-rose-700 border-rose-200",
};

export default function EmployerInterviews() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [scoring, setScoring] = useState(null);
  const load = async () => {
    try { const {data} = await api.get("/interviews"); setRows(data); }
    catch (e) { toast.error("Failed to load interviews"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-6 py-3 flex items-center gap-3">
        <button onClick={()=>nav("/employer/dashboard")} className="text-xs inline-flex items-center gap-1" data-testid="emp-iv-back"><ArrowLeft size={12}/> Back</button>
        <div className="font-display font-bold text-sm">Interviews</div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Wave 3 · Interviews</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Your interview requests</h1>
          <p className="text-sm text-slate-500 mt-1">Track candidate responses, mark completed, and submit scorecards.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Interview</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Candidate response</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(i => (
                <tr key={i.id} data-testid={`emp-iv-row-${i.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-500"><Video size={13}/></div>
                      <div>
                        <div className="font-semibold text-slate-900 text-xs">Candidate {i.candidate_id?.slice(0,8)}</div>
                        <div className="text-[10px] text-slate-500">{i.interview_type}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{i.scheduled_at ? new Date(i.scheduled_at).toLocaleString() : "TBD"}</td>
                  <td className="px-4 py-3"><span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${STAT_TONE[i.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{i.status}</span></td>
                  <td className="px-4 py-3 text-xs">
                    {i.candidate_interested === true && (
                      <div>
                        <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={11}/> Interested</span>
                        {i.candidate_availability?.length>0 && <div className="text-[10px] text-slate-500 mt-0.5">{i.candidate_availability.join(" · ")}</div>}
                      </div>
                    )}
                    {i.candidate_interested === false && <span className="text-rose-700 font-semibold">Declined</span>}
                    {(i.candidate_interested === undefined || i.candidate_interested === null) && <span className="text-slate-400">Awaiting</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`emp-iv-score-${i.id}`} onClick={()=>setScoring(i)} className="text-[10px] px-2 py-0.5 border border-sky-300 bg-sky-50 text-sky-700 rounded font-semibold hover:bg-sky-100 inline-flex items-center gap-1">
                      <ClipboardCheck size={10}/> Scorecard
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-sm text-slate-500">No interviews yet — request one from your matches page.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>
      {scoring && <InterviewScorecard interview={scoring} onClose={()=>setScoring(null)} onSubmitted={load}/>}
      <Toaster position="top-right" richColors/>
    </div>
  );
}
